/**
 * Whole cents, as `Math.round(value * 100)`: the rounding every budget total has always used.
 * Every budget view rounds through this one helper so they agree with each other. Exact decimal
 * line math, which settles half cents such as 10.075, comes with a dedicated money change.
 */
export const toBudgetCents = (value: number): number =>
  Number.isFinite(value) ? Math.round(value * 100) || 0 : value;

export const roundBudgetMoney = (value: number): number => toBudgetCents(value) / 100;
const toBudgetNumber = (value: number | string | null | undefined): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export interface BudgetFringeInput {
  budget_fringe_id: number;
  rate?: number | string | null;
  unit?: string;
  ceiling?: number | string | null;
  ceiling_type?: string | null;
  name?: string;
}

export interface BudgetFringeEntry {
  categoryIndex: number;
  /** Set on every entry, or on none, to also split each category's share across its accounts. */
  accountIndex?: number;
  estimate: number;
  actual: number;
  quantityInHours: number;
  fringes?: BudgetFringeInput[];
}

export interface BudgetFringeAmounts {
  estimate: number;
  actual: number;
}

export interface BudgetFringeDetail extends BudgetFringeAmounts {
  fringe: BudgetFringeInput;
  /** This fringe's share for each category, by category index. Shares add up to the fringe. */
  categories: BudgetFringeAmounts[];
}

export interface BudgetFringeTotals extends BudgetFringeAmounts {
  categories: BudgetFringeAmounts[];
  /** By account index, when entries carry one. A category's accounts add up to its share. */
  accounts: BudgetFringeAmounts[];
  fringes: BudgetFringeDetail[];
}

const getFringeCeilingType = (fringe: BudgetFringeInput): "individual" | "aggregate" => {
  if (fringe.ceiling_type === "individual" || fringe.ceiling_type === "aggregate") {
    return fringe.ceiling_type;
  }
  if (fringe.ceiling_type == null && fringe.ceiling == null) return "individual";
  if (fringe.ceiling_type == null) {
    throw new Error(
      "A fringe with a saved ceiling needs an explicit individual or aggregate ceiling type. Verify the fringe before replacing or copying this budget."
    );
  }
  throw new Error("Fringe ceiling type must be individual or aggregate.");
};

/** Allocate already-rounded money with deterministic largest-remainder cents. */
function allocateMoney(total: number, weights: number[]): number[] {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightTotal <= 0) return weights.map(() => 0);
  const cents = Math.round(total * 100);
  const shares = weights.map((weight, index) => {
    const exact = (cents * weight) / weightTotal;
    return { index, cents: Math.floor(exact), remainder: exact - Math.floor(exact) };
  });
  let remainder = cents - shares.reduce((sum, share) => sum + share.cents, 0);
  for (const share of [...shares].sort((a, b) => b.remainder - a.remainder || a.index - b.index)) {
    if (remainder <= 0) break;
    share.cents += 1;
    remainder -= 1;
  }
  return shares.map((share) => share.cents / 100);
}

const timeUnitHours = (unit: string | undefined): number => {
  if (unit === "weeks") return 40;
  if (unit === "days") return 8;
  if (unit === "hours") return 1;
  return 0;
};

/**
 * Aggregate ceilings apply once per saved fringe across the entire budget.
 * Individual ceilings apply per linked item. Time-based actuals use the saved
 * item quantities, matching the application's existing fringe calculation.
 */
export function calculateBudgetFringeTotals(
  entries: BudgetFringeEntry[],
  categoryCount: number,
  accountCount = 0
): BudgetFringeTotals {
  const categories = Array.from({ length: categoryCount }, () => ({ estimate: 0, actual: 0 }));
  const accounts = Array.from({ length: accountCount }, () => ({ estimate: 0, actual: 0 }));
  const fringes: BudgetFringeTotals["fringes"] = [];
  const groups = new Map<number, { fringe: BudgetFringeInput; entries: BudgetFringeEntry[] }>();
  for (const entry of entries) {
    for (const fringe of entry.fringes ?? []) {
      const group = groups.get(fringe.budget_fringe_id);
      if (group) group.entries.push(entry);
      else groups.set(fringe.budget_fringe_id, { fringe, entries: [entry] });
    }
  }

  for (const { fringe, entries: linkedEntries } of groups.values()) {
    const detail: BudgetFringeDetail = {
      fringe,
      estimate: 0,
      actual: 0,
      categories: categories.map(() => ({ estimate: 0, actual: 0 })),
    };
    const ceilingType = getFringeCeilingType(fringe);
    const rate = toBudgetNumber(fringe.rate);
    const ceiling = fringe.ceiling == null ? Number.MAX_VALUE : toBudgetNumber(fringe.ceiling);
    const hours = timeUnitHours(fringe.unit);
    const valueFor = (entry: BudgetFringeEntry, actual: boolean): number =>
      fringe.unit === "percent"
        ? actual
          ? entry.actual
          : entry.estimate
        : hours > 0
          ? entry.quantityInHours / hours
          : 0;
    const costFor = (value: number): number =>
      (Math.min(value, ceiling) * rate) / (fringe.unit === "percent" ? 100 : 1);

    for (const field of ["estimate", "actual"] as const) {
      const values = linkedEntries.map((entry) => valueFor(entry, field === "actual"));
      // Individual ceilings share by each item's capped cost, aggregate ones by its wage base.
      const entryWeights = values.map((value) =>
        ceilingType === "individual" ? costFor(value) : value
      );
      const total =
        ceilingType === "individual"
          ? entryWeights.reduce((sum, weight) => sum + weight, 0)
          : costFor(values.reduce((sum, value) => sum + value, 0));
      const weights = categories.map(() => 0);
      linkedEntries.forEach((entry, index) => {
        weights[entry.categoryIndex] += entryWeights[index];
      });
      const allocations = allocateMoney(roundBudgetMoney(total), weights);
      detail[field] = roundBudgetMoney(total);
      categories.forEach((category, index) => {
        detail.categories[index][field] = allocations[index];
        category[field] = roundBudgetMoney(category[field] + allocations[index]);
      });

      // Each account splits its category's rounded share, so accounts add up to the category.
      categories.forEach((_, categoryIndex) => {
        const accountWeights = new Map<number, number>();
        linkedEntries.forEach((entry, index) => {
          if (entry.categoryIndex !== categoryIndex || entry.accountIndex === undefined) return;
          const weight = (accountWeights.get(entry.accountIndex) ?? 0) + entryWeights[index];
          accountWeights.set(entry.accountIndex, weight);
        });
        const shares = allocateMoney(allocations[categoryIndex], [...accountWeights.values()]);
        [...accountWeights.keys()].forEach((accountIndex, index) => {
          accounts[accountIndex][field] = roundBudgetMoney(
            accounts[accountIndex][field] + shares[index]
          );
        });
      });
    }
    fringes.push(detail);
  }

  return {
    estimate: roundBudgetMoney(categories.reduce((sum, category) => sum + category.estimate, 0)),
    actual: roundBudgetMoney(categories.reduce((sum, category) => sum + category.actual, 0)),
    categories,
    accounts,
    fringes,
  };
}

interface BudgetTimeItem {
  quantity?: number | string | null;
  unit?: string | null;
  children?: BudgetTimeItem[] | null;
}

/**
 * A parent's time is its children's, as its estimate is; its own quantity counts only when it
 * has no children.
 */
export function budgetItemQuantityInHours(item: BudgetTimeItem): number {
  const children = item.children ?? [];
  if (children.length > 0) {
    return children.reduce((sum, child) => sum + budgetItemQuantityInHours(child), 0);
  }
  return toBudgetNumber(item.quantity) * timeUnitHours(item.unit ?? undefined);
}
